import React, { useState, useEffect } from 'react';
import { X, ShoppingBag, Trash2, Plus, Minus, ArrowRight, CheckCircle2, MapPin, User, Phone, ShieldCheck } from 'lucide-react';

const API_BASE_URL = 'https://rudrakshenterprises.liveindiatech.com/rudraksh_backend';

export default function CartDrawer({ isOpen, setIsOpen, cart, setCart }) {
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [orderPlaced, setOrderPlaced] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        address: '',
        city: '',
        pincode: '',
        paymentMethod: 'COD'
    });
    const [loading, setLoading] = useState(false);

    // Page load hone par localStorage se cart load karein
    useEffect(() => {
        const savedCart = localStorage.getItem('rudraksh_cart');
        if (savedCart) {
            try {
                setCart(JSON.parse(savedCart));
            } catch (e) {
                console.error("Error parsing cart from localStorage", e);
            }
        }
    }, [setCart]);

    // Cart update hone par localStorage mein save karein
    useEffect(() => {
        localStorage.setItem('rudraksh_cart', JSON.stringify(cart));
    }, [cart]);

    if (!isOpen) return null;

    const increaseQty = (id) => {
        setCart(cart.map(item =>
            item.id === id ? { ...item, quantity: item.quantity + 1 } : item
        ));
    };

    const decreaseQty = (id) => {
        setCart(cart.map(item =>
            item.id === id && item.quantity > 1 ? { ...item, quantity: item.quantity - 1 } : item
        ));
    };

    const removeItem = (id) => {
        setCart(cart.filter(item => item.id !== id));
    };

    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const shippingFee = cart.length > 0 ? 50 : 0;
    const grandTotal = subtotal + (subtotal > 999 ? 0 : shippingFee);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const saveOrderToBackend = async (paymentId = 'COD') => {
        const orderData = {
            customer_name: formData.name,
            phone_number: formData.phone,
            address: `${formData.address}, ${formData.city} - ${formData.pincode}`,
            payment_method: formData.paymentMethod,
            payment_id: paymentId,
            total_amount: grandTotal,
            products_json: cart,
            items: cart.map(item => ({
                product_name: item.name,
                price: item.price,
                quantity: item.quantity
            }))
        };

        try {
            await fetch('https://rudrakshenterprises.liveindiatech.com/rudraksh_backend/api/orders/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(orderData)
            });
            setOrderPlaced(true);
            setCart([]);
            localStorage.removeItem('rudraksh_cart');
        } catch (error) {
            console.error('Order saving error:', error);
            setOrderPlaced(true);
            setCart([]);
            localStorage.removeItem('rudraksh_cart');
        } finally {
            setLoading(false);
        }
    };

    const handlePlaceOrder = (e) => {
        e.preventDefault();
        setLoading(true);

        if (formData.paymentMethod === 'Online') {
            const options = {
                key: "rzp_test_YOUR_KEY_HERE",
                amount: grandTotal * 100,
                currency: "INR",
                name: "Rudraksh Enterprises",
                description: "Pure & Natural Wellness Products",
                image: "/logo.png",
                handler: function (response) {
                    alert("Payment Successful! Payment ID: " + response.razorpay_payment_id);
                    saveOrderToBackend(response.razorpay_payment_id);
                },
                prefill: {
                    name: formData.name,
                    email: "support@rudrakshenterprises.com",
                    contact: formData.phone
                },
                theme: {
                    color: "#ea580c"
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', function (response) {
                alert("Payment Failed: " + response.error.description);
                setLoading(false);
            });
            rzp.open();
        } else {
            saveOrderToBackend('COD');
        }
    };

    const resetCartDrawer = () => {
        setOrderPlaced(false);
        setIsCheckingOut(false);
        setIsOpen(false);
    };

    return (
        <div className="fixed inset-0 z-[9999] overflow-hidden">
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={() => setIsOpen(false)}
            />

            <div className="absolute inset-y-0 right-0 max-w-full flex pl-4 sm:pl-10 z-[10000]">
                <div className="w-screen max-w-sm sm:max-w-md bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out h-full">

                    <div className="p-4 bg-orange-600 text-white flex items-center justify-between shadow-sm">
                        <div className="flex items-center gap-2">
                            <ShoppingBag size={20} />
                            <h2 className="font-bold text-base sm:text-lg">
                                {orderPlaced ? 'Order Confirmed' : isCheckingOut ? 'Checkout Details' : `Shopping Cart (${cart.reduce((acc, item) => acc + item.quantity, 0)})`}
                            </h2>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="text-orange-100 hover:text-white p-1.5 rounded-full hover:bg-orange-700 transition cursor-pointer border-none bg-transparent"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-28">
                        {orderPlaced ? (
                            <div className="text-center py-16 space-y-4">
                                <CheckCircle2 size={64} className="mx-auto text-green-600 animate-bounce" />
                                <h3 className="text-xl font-black text-gray-900">Order Placed Successfully!</h3>
                                <p className="text-xs text-gray-500 px-6">
                                    Thank you for shopping with <strong className="text-orange-600">Rudraksh Enterprises</strong>. Your order has been registered and is being processed.
                                </p>
                                <div className="pt-6">
                                    <button
                                        onClick={resetCartDrawer}
                                        className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-3 rounded-xl text-xs transition shadow cursor-pointer border-none"
                                    >
                                        Continue Shopping
                                    </button>
                                </div>
                            </div>
                        ) : isCheckingOut ? (
                            <form onSubmit={handlePlaceOrder} className="space-y-3">
                                <div className="bg-orange-50 p-3 rounded-2xl border border-orange-100 text-xs text-orange-800 font-bold mb-4 flex items-center gap-1.5">
                                    <ShieldCheck size={16} className="text-orange-600" /> Secure Checkout & Shipping Info
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1 flex items-center gap-1">
                                        <User size={12} /> Full Name
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        required
                                        placeholder="Enter your full name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-200 rounded-xl p-2.5 text-xs outline-none bg-gray-50 focus:ring-2 focus:ring-orange-500 text-gray-800 font-medium"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1 flex items-center gap-1">
                                        <Phone size={12} /> Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        required
                                        placeholder="10-digit mobile number"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-200 rounded-xl p-2.5 text-xs outline-none bg-gray-50 focus:ring-2 focus:ring-orange-500 text-gray-800 font-medium"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1 flex items-center gap-1">
                                        <MapPin size={12} /> Street Address
                                    </label>
                                    <input
                                        type="text"
                                        name="address"
                                        required
                                        placeholder="House No., Street, Locality"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-200 rounded-xl p-2.5 text-xs outline-none bg-gray-50 focus:ring-2 focus:ring-orange-500 text-gray-800 font-medium"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1">City</label>
                                        <input
                                            type="text"
                                            name="city"
                                            required
                                            placeholder="City"
                                            value={formData.city}
                                            onChange={handleInputChange}
                                            className="w-full border border-gray-200 rounded-xl p-2.5 text-xs outline-none bg-gray-50 focus:ring-2 focus:ring-orange-500 text-gray-800 font-medium"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 mb-1">Pincode</label>
                                        <input
                                            type="text"
                                            name="pincode"
                                            required
                                            placeholder="Pincode"
                                            value={formData.pincode}
                                            onChange={handleInputChange}
                                            className="w-full border border-gray-200 rounded-xl p-2.5 text-xs outline-none bg-gray-50 focus:ring-2 focus:ring-orange-500 text-gray-800 font-medium"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Payment Method</label>
                                    <select
                                        name="paymentMethod"
                                        value={formData.paymentMethod}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-200 rounded-xl p-2.5 text-xs outline-none bg-gray-50 focus:ring-2 focus:ring-orange-500 text-gray-800 font-bold cursor-pointer"
                                    >
                                        <option value="COD">Cash on Delivery (COD)</option>
                                        <option value="Online">Pay Online (Razorpay - UPI / Card / NetBanking)</option>
                                    </select>
                                </div>

                                <div className="pt-2 flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setIsCheckingOut(false)}
                                        className="w-1/3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-xl text-xs transition cursor-pointer border-none"
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-2/3 bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 rounded-xl shadow transition text-xs flex items-center justify-center gap-1.5 cursor-pointer border-none"
                                    >
                                        {loading ? 'Processing...' : formData.paymentMethod === 'Online' ? `Pay ₹${grandTotal} Now` : `Place Order (COD)`}
                                    </button>
                                </div>
                            </form>
                        ) : cart.length === 0 ? (
                            <div className="text-center py-24 text-gray-400 space-y-3">
                                <ShoppingBag size={48} className="mx-auto text-gray-200 stroke-1" />
                                <p className="text-sm font-bold text-gray-700">Your cart is empty!</p>
                                <p className="text-xs text-gray-400">Add your favorite pure wellness items.</p>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="mt-2 bg-orange-600 text-white text-xs font-bold px-5 py-2.5 rounded-xl shadow hover:bg-orange-700 transition cursor-pointer border-none"
                                >
                                    Explore Products
                                </button>
                            </div>
                        ) : (
                            cart.map(item => (
                                <div key={item.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-2xl border border-gray-100 gap-3">
                                    <div className="w-14 h-14 bg-white rounded-xl border border-gray-200 overflow-hidden flex items-center justify-center shrink-0">
                                        <img
                                            src={item.image}
                                            onError={(e) => { e.target.onerror = null; e.target.src = item.fallbackImage || 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80'; }}
                                            alt={item.name}
                                            className="w-full h-full object-contain p-0.5"
                                        />
                                    </div>

                                    <div className="flex-1 min-w-0 text-left">
                                        <h4 className="font-extrabold text-xs text-gray-900 truncate">{item.name}</h4>
                                        <p className="text-xs text-orange-600 font-black mt-0.5">₹{item.price}</p>

                                        <div className="flex items-center gap-3 mt-2">
                                            <div className="flex items-center border border-gray-200 rounded-lg bg-white overflow-hidden">
                                                <button
                                                    onClick={() => decreaseQty(item.id)}
                                                    className="px-2 py-1 text-gray-600 hover:bg-gray-100 transition text-xs cursor-pointer border-none bg-transparent"
                                                >
                                                    <Minus size={12} />
                                                </button>
                                                <span className="px-2.5 py-0.5 text-xs font-bold text-gray-800">{item.quantity}</span>
                                                <button
                                                    onClick={() => increaseQty(item.id)}
                                                    className="px-2 py-1 text-gray-600 hover:bg-gray-100 transition text-xs cursor-pointer border-none bg-transparent"
                                                >
                                                    <Plus size={12} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => removeItem(item.id)}
                                        className="text-gray-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition shrink-0 cursor-pointer border-none bg-transparent"
                                        title="Remove item"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>

                    {!orderPlaced && cart.length > 0 && !isCheckingOut && (
                        <div className="absolute bottom-0 right-0 w-full max-w-sm sm:max-w-md p-4 sm:p-5 border-t border-gray-200 bg-white shadow-2xl space-y-3 z-[10001]">
                            <div className="space-y-1.5 text-xs text-gray-700 border-b border-gray-200 pb-3 font-semibold">
                                <div className="flex justify-between">
                                    <span>Subtotal:</span>
                                    <span className="font-bold text-gray-900">₹{subtotal}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Shipping:</span>
                                    <span className="font-bold text-green-600">{subtotal > 999 ? 'FREE' : `₹${shippingFee}`}</span>
                                </div>
                            </div>

                            <div className="flex justify-between text-sm sm:text-base font-black text-gray-900 pt-1">
                                <span>Grand Total:</span>
                                <span className="text-orange-600">₹{grandTotal}</span>
                            </div>

                            <button
                                onClick={() => setIsCheckingOut(true)}
                                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3.5 rounded-xl shadow-lg transition text-xs sm:text-sm flex items-center justify-center gap-2 cursor-pointer border-none"
                            >
                                Proceed to Checkout <ArrowRight size={16} />
                            </button>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}